/**
 * Client for sending recorded audio to the /api/transcribe endpoint.
 * Reads the audio file as base64, POSTs to the server, returns transcribed text.
 */

import { File as ExpoFile } from 'expo-file-system';

import { authenticatedFetch } from '@/lib/services/AuthenticatedFetchService';
import type { TranscriptionResponse } from '@/types/voice-transcription';

/**
 * Convert an ArrayBuffer to a base64-encoded string.
 */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export class AudioTranscriptionClient {
  private static instance: AudioTranscriptionClient;

  static getInstance(): AudioTranscriptionClient {
    if (!AudioTranscriptionClient.instance) {
      AudioTranscriptionClient.instance = new AudioTranscriptionClient();
    }
    return AudioTranscriptionClient.instance;
  }

  /**
   * Transcribe an audio file at the given URI.
   * @param fileUri - Local file URI from expo-av recording
   * @param mimeType - Audio MIME type (default: 'audio/m4a')
   * @returns Transcribed text string
   * @throws Error if transcription fails
   */
  async transcribe(fileUri: string, mimeType = 'audio/m4a'): Promise<string> {
    // Read audio file as base64 using expo-file-system v19 File API
    const file = new ExpoFile(fileUri);
    const buffer = await file.arrayBuffer();
    const base64Audio = arrayBufferToBase64(buffer);

    // POST to transcription API
    const response = await authenticatedFetch.fetch('/api/transcribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        audio: base64Audio,
        mimeType,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = (errorData as { error?: string }).error || 'Transcription failed';
      throw new TranscriptionError(errorMessage, response.status);
    }

    const data: TranscriptionResponse = await response.json();
    return data.text;
  }
}

export class TranscriptionError extends Error {
  constructor(
    message: string,
    public readonly statusCode: number,
  ) {
    super(message);
    this.name = 'TranscriptionError';
  }
}

export const audioTranscriptionClient = AudioTranscriptionClient.getInstance();
